import { useEffect, useState } from "react";
import {
  Lock, Unlock, ChevronDown, UserPlus, Check, Users, AlertTriangle,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import {
  listChaptersRaw,
  getClassTopicControls,
  updateClassTopicControls,
  toggleTopicException,
  teacherStudentList,
  getTopicsEngagement,
} from "@/lib/api";

/* ─── State helpers ─────────────────────────────────────────────────────────
 *
 *  "locked"         → topic not in unlocked_topics, no student_unlocks
 *  "class_unlocked" → topic is in unlocked_topics
 *  "students_only"  → topic not in unlocked_topics, but ≥1 student_unlocks
 *
 * ─────────────────────────────────────────────────────────────────────────── */
function topicState(topicName, controls) {
  const classUnlocked = (controls.unlocked_topics || []).includes(topicName);
  if (classUnlocked) return "class_unlocked";
  const studentIds = studentUnlockIds(topicName, controls);
  return studentIds.length > 0 ? "students_only" : "locked";
}

function studentUnlockIds(topicName, controls) {
  return (controls.student_unlocks || [])
    .filter(e => (e.unlocked_topics || []).includes(topicName))
    .map(e => e.learner_id);
}

/* ─── Badge ─────────────────────────────────────────────────────────────────── */
function StateBadge({ state, count, onManage, isExpanded }) {
  if (state === "class_unlocked")
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border bg-emerald-100 text-emerald-700 border-emerald-200 flex-shrink-0">
        <Check className="w-3.5 h-3.5" />
        Open to class
      </span>
    );
  if (state === "students_only")
    return (
      <button
        onClick={onManage}
        data-testid={onManage ? "students-unlocked-badge" : undefined}
        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border bg-indigo-100 text-indigo-700 border-indigo-200 flex-shrink-0 hover:bg-indigo-200 transition cursor-pointer"
      >
        <Users className="w-3.5 h-3.5" />
        {count} student{count !== 1 ? "s" : ""} unlocked
        <ChevronDown className={`w-3 h-3 opacity-60 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
      </button>
    );
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border bg-slate-100 text-slate-600 border-slate-200 flex-shrink-0">
      <Lock className="w-3.5 h-3.5" />
      Locked
    </span>
  );
}

/* ─── Avatar chip ────────────────────────────────────────────────────────────── */
function Avatar({ name, color = "slate" }) {
  const cls = {
    slate:  "bg-slate-200 text-slate-700",
    indigo: "bg-indigo-100 text-indigo-700",
  }[color] || "bg-slate-200 text-slate-700";
  return (
    <span className={`w-6 h-6 rounded-full text-xs flex items-center justify-center font-bold flex-shrink-0 ${cls}`}>
      {(name || "?").slice(0, 1).toUpperCase()}
    </span>
  );
}

/* ─── Student picker popover content (multi-select) ─────────────────────────── */
function StudentPicker({ students, unlockedIds = [], onUnlock }) {
  const [pending, setPending] = useState([]);

  const toggle = (learnerId) =>
    setPending(prev =>
      prev.includes(learnerId) ? prev.filter(id => id !== learnerId) : [...prev, learnerId]
    );

  const handleUnlock = () => {
    if (!pending.length) return;
    onUnlock(pending);
    setPending([]);
  };

  return (
    <PopoverContent align="start" className="w-64 p-2">
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-2 pt-1 pb-2">
        Students
      </div>
      <div className="max-h-48 overflow-y-auto space-y-0.5">
        {students.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-3">
            No students in this class
          </p>
        ) : (
          students.map(s => {
            const alreadyUnlocked = unlockedIds.includes(s.learner_id);
            const isChecked = pending.includes(s.learner_id);
            if (alreadyUnlocked) {
              return (
                <div
                  key={s.learner_id}
                  className="w-full flex items-center gap-2 text-sm px-2 py-1.5 rounded bg-emerald-50 cursor-default"
                >
                  <span className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-white" />
                  </span>
                  <span className="text-emerald-700 font-medium">{s.name || s.learner_id}</span>
                </div>
              );
            }
            return (
              <button
                key={s.learner_id}
                data-testid={`pick-student-${s.learner_id}`}
                onClick={() => toggle(s.learner_id)}
                className={`w-full flex items-center gap-2 text-left text-sm px-2 py-1.5 rounded transition ${
                  isChecked ? "bg-indigo-50" : "hover:bg-slate-100"
                } text-slate-800`}
              >
                <span className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition ${
                  isChecked ? "border-indigo-500 bg-indigo-500" : "border-slate-300 bg-white"
                }`}>
                  {isChecked && <Check className="w-2.5 h-2.5 text-white" />}
                </span>
                {s.name || s.learner_id}
              </button>
            );
          })
        )}
      </div>
      <div className="pt-2 mt-1 border-t border-slate-100">
        <button
          onClick={handleUnlock}
          disabled={!pending.length}
          data-testid="picker-unlock-selected"
          className="w-full text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 px-3 py-1.5 rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {pending.length > 0 ? `Unlock selected (${pending.length})` : "Unlock selected"}
        </button>
      </div>
    </PopoverContent>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 *  ClassTopicsTab
 * ═══════════════════════════════════════════════════════════════════════════ */
export function ClassTopicsTab({ teacherId, grade, subject }) {
  const [chapters, setChapters]               = useState([]);
  const [controls, setControls]               = useState({ unlocked_topics: [], student_unlocks: [] });
  const [allStudents, setAllStudents]         = useState([]);
  const [engagement, setEngagement]           = useState({ by_subtopic: {}, by_topic: {} });
  const [expandedTopics, setExpanded]         = useState(new Set());
  const [pickerTopic, setPickerTopic]         = useState(null);   // topic name with open student picker (expanded body)
  const [rowPickerTopic, setRowPickerTopic]   = useState(null);   // topic name with open student picker (header row)
  const [relockConfirm, setRelockConfirm]     = useState(null);   // topic name awaiting re-lock confirm
  const [loading, setLoading]                 = useState(true);
  const [saving, setSaving]                   = useState(false);

  /* ─── Load on mount ─────────────────────────────────────────────────────── */
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [chaps, ctrl, studs, eng] = await Promise.all([
          listChaptersRaw(subject, grade, teacherId),
          getClassTopicControls(teacherId, grade, subject),
          teacherStudentList(teacherId, grade, subject),
          getTopicsEngagement(teacherId, grade, subject),
        ]);
        if (!cancelled) {
          setChapters(chaps);
          setControls(ctrl || { unlocked_topics: [], student_unlocks: [] });
          setAllStudents(studs?.students || []);
          setEngagement(eng || { by_subtopic: {}, by_topic: {} });
        }
      } catch {
        if (!cancelled) toast.error("Failed to load topics");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [teacherId, grade, subject]);

  /* ─── Expand / collapse ─────────────────────────────────────────────────── */
  const toggleExpand = (topicName) =>
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(topicName) ? next.delete(topicName) : next.add(topicName);
      return next;
    });

  /* ─── Action 1: Unlock for class ────────────────────────────────────────── */
  const unlockForClass = async (topicName) => {
    if (saving) return;
    setSaving(true);
    const newUnlocked = [...new Set([...(controls.unlocked_topics || []), topicName])];
    // Class unlock supersedes student unlocks — clean them out
    const newStudentUnlocks = (controls.student_unlocks || [])
      .map(e => ({ ...e, unlocked_topics: (e.unlocked_topics || []).filter(t => t !== topicName) }))
      .filter(e => e.unlocked_topics.length > 0);
    try {
      const updated = await updateClassTopicControls(teacherId, grade, subject, newUnlocked, newStudentUnlocks);
      setControls(updated);
      toast.success(`${topicName} unlocked for the whole class`);
    } catch {
      toast.error("Failed to unlock topic");
    } finally {
      setSaving(false);
    }
  };

  /* ─── Action 2: Re-lock for class ───────────────────────────────────────── */
  const handleRelockClick = (topicName) => {
    const hasStudentUnlocks = studentUnlockIds(topicName, controls).length > 0;
    if (hasStudentUnlocks) {
      setRelockConfirm(topicName);   // show inline confirmation
    } else {
      execRelock(topicName);
    }
  };

  /* Called from the collapsed header row — auto-expands when confirm needed */
  const handleRelockFromRow = (topicName) => {
    const hasStudentUnlocks = studentUnlockIds(topicName, controls).length > 0;
    if (hasStudentUnlocks) {
      setExpanded(prev => { const n = new Set(prev); n.add(topicName); return n; });
      setRelockConfirm(topicName);
    } else {
      execRelock(topicName);
    }
  };

  const execRelock = async (topicName) => {
    if (saving) return;
    setSaving(true);
    setRelockConfirm(null);
    const newUnlocked = (controls.unlocked_topics || []).filter(t => t !== topicName);
    const newStudentUnlocks = (controls.student_unlocks || [])
      .map(e => ({ ...e, unlocked_topics: (e.unlocked_topics || []).filter(t => t !== topicName) }))
      .filter(e => e.unlocked_topics.length > 0);
    try {
      const updated = await updateClassTopicControls(teacherId, grade, subject, newUnlocked, newStudentUnlocks);
      setControls(updated);
      toast.success(`${topicName} re-locked — access removed for all students`);
    } catch {
      toast.error("Failed to re-lock topic");
    } finally {
      setSaving(false);
    }
  };

  /* ─── Action 3: Unlock for specific student(s) ──────────────────────────── */
  const unlockForStudents = async (topicName, learnerIds) => {
    if (!learnerIds.length) return;
    try {
      let updated = controls;
      for (const learnerId of learnerIds) {
        updated = await toggleTopicException(teacherId, grade, subject, learnerId, topicName, "add");
      }
      setControls(updated);
      setPickerTopic(null);
      setRowPickerTopic(null);
      const names = learnerIds.map(id => allStudents.find(s => s.learner_id === id)?.name || id);
      toast.success(
        learnerIds.length === 1
          ? `${names[0]} can now access ${topicName}`
          : `${learnerIds.length} students can now access ${topicName}`
      );
    } catch {
      toast.error("Failed to add student unlock");
    }
  };

  /* ─── Action 4: Remove individual student unlock ────────────────────────── */
  const removeStudentUnlock = async (topicName, learnerId) => {
    try {
      const updated = await toggleTopicException(teacherId, grade, subject, learnerId, topicName, "remove");
      setControls(updated);
      const student = allStudents.find(s => s.learner_id === learnerId);
      toast.success(`${student?.name || learnerId}'s access to ${topicName} removed`);
    } catch {
      toast.error("Failed to remove student unlock");
    }
  };

  /* ─── Loading / empty ───────────────────────────────────────────────────── */
  if (loading)
    return <div className="py-16 text-center text-slate-400 text-sm">Loading topics…</div>;
  if (!chapters.length)
    return <div className="py-16 text-center text-slate-400 text-sm">No topics found for this grade and subject.</div>;

  /* ─── Render ─────────────────────────────────────────────────────────────── */
  return (
    <div className="space-y-1.5" data-testid="class-topics-tab">
      {chapters.map(ch => {
        const topicName  = ch.chapter;
        if (!topicName) return null;

        const state      = topicState(topicName, controls);
        const studIds    = studentUnlockIds(topicName, controls);
        const expanded   = expandedTopics.has(topicName);

        // Students NOT yet unlocked for this topic (for the picker)
        const pickable   = allStudents.filter(s => !studIds.includes(s.learner_id));

        /* Row background */
        const rowBg = {
          class_unlocked: "bg-emerald-50/50 border-emerald-200",
          students_only:  "bg-indigo-50/40 border-indigo-200",
          locked:         "bg-white border-slate-200",
        }[state];

        /* Divider color inside expanded area */
        const dividerColor = {
          class_unlocked: "border-emerald-100",
          students_only:  "border-indigo-100",
          locked:         "border-slate-100",
        }[state];

        return (
          <div
            key={topicName}
            data-testid={`topic-row-${topicName.replace(/\s+/g, "-")}`}
            className={`rounded-xl border overflow-hidden transition-colors ${rowBg}`}
          >
            {/* ── Header row ── */}
            <div className="flex items-center gap-2 px-4 py-3">
              <button
                onClick={() => toggleExpand(topicName)}
                data-testid={`topic-expand-${topicName.replace(/\s+/g, "-")}`}
                className="flex items-center gap-2 flex-1 min-w-0 text-left"
              >
                <ChevronDown
                  className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${expanded ? "" : "-rotate-90"}`}
                />
                <span className="font-semibold text-slate-900 text-sm truncate">{topicName}</span>
                <span className="text-xs text-slate-400 ml-1 flex-shrink-0 hidden sm:inline">
                  {ch.sub_topic_count} sub-topics
                </span>
              </button>

              {/* Learner count from sessions — hover for names */}
              {engagement.by_topic[topicName] > 0 ? (
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        className="text-xs text-slate-600 font-medium mr-3 hidden md:block tabular-nums cursor-default underline decoration-dotted decoration-slate-300"
                        data-testid={`topic-engagement-count-${topicName.replace(/\s+/g, "-")}`}
                      >
                        {engagement.by_topic[topicName]}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 max-w-[220px]"
                      data-testid={`topic-engagement-tooltip-${topicName.replace(/\s+/g, "-")}`}
                    >
                      <div className="font-semibold mb-1 text-slate-300">Active in this topic</div>
                      {(engagement.by_topic_students?.[topicName] || []).map((s) => (
                        <div key={s.learner_id}>{s.name}</div>
                      ))}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <span className="text-xs text-slate-400 mr-3 hidden md:block tabular-nums">–</span>
              )}

              <StateBadge
                state={state}
                count={studIds.length}
                isExpanded={expanded}
                onManage={state === "students_only" ? () => {
                  setExpanded(prev => {
                    const n = new Set(prev);
                    if (n.has(topicName)) {
                      n.delete(topicName);
                    } else {
                      n.add(topicName);
                      setTimeout(() => {
                        document.getElementById(`student-unlocks-${topicName.replace(/\s+/g, "-")}`)
                          ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
                      }, 80);
                    }
                    return n;
                  });
                } : undefined}
              />

              {/* ── Inline action buttons (always visible on collapsed row) ── */}
              {state === "locked" && (
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => unlockForClass(topicName)}
                    disabled={saving}
                    data-testid={`row-unlock-class-${topicName.replace(/\s+/g, "-")}`}
                    className="flex items-center gap-1.5 text-xs font-semibold text-slate-800 px-2.5 py-1 border border-slate-300 bg-white rounded-lg hover:bg-slate-50 transition disabled:opacity-50"
                  >
                    <Unlock className="w-3 h-3" />
                    <span className="hidden sm:inline">Unlock class</span>
                  </button>
                  <Popover
                    open={rowPickerTopic === topicName}
                    onOpenChange={open => setRowPickerTopic(open ? topicName : null)}
                  >
                    <PopoverTrigger asChild>
                      <button
                        data-testid={`row-unlock-student-${topicName.replace(/\s+/g, "-")}`}
                        className="flex items-center gap-1.5 text-xs font-semibold text-indigo-700 px-2.5 py-1 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition"
                      >
                        <UserPlus className="w-3 h-3" />
                        <span className="hidden sm:inline">Individual unlock</span>
                      </button>
                    </PopoverTrigger>
                    <StudentPicker
                      students={allStudents}
                      onUnlock={learnerIds => unlockForStudents(topicName, learnerIds)}
                    />
                  </Popover>
                </div>
              )}

              {state === "class_unlocked" && (
                <button
                  onClick={() => handleRelockFromRow(topicName)}
                  disabled={saving}
                  data-testid={`row-relock-${topicName.replace(/\s+/g, "-")}`}
                  className="flex items-center gap-1.5 text-xs font-semibold text-rose-700 px-2.5 py-1 border border-rose-300 rounded-lg hover:bg-rose-50 transition disabled:opacity-50 flex-shrink-0"
                >
                  <Lock className="w-3 h-3" />
                  <span className="hidden sm:inline">Re-lock</span>
                </button>
              )}

              {state === "students_only" && (
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => unlockForClass(topicName)}
                    disabled={saving}
                    data-testid={`row-unlock-class-so-${topicName.replace(/\s+/g, "-")}`}
                    className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 px-2.5 py-1 border border-slate-300 bg-white rounded-lg hover:bg-slate-50 transition disabled:opacity-50"
                  >
                    <Unlock className="w-3 h-3" />
                    <span className="hidden sm:inline">Unlock class</span>
                  </button>
                  <Popover
                    open={rowPickerTopic === topicName}
                    onOpenChange={open => setRowPickerTopic(open ? topicName : null)}
                  >
                    <PopoverTrigger asChild>
                      <button
                        data-testid={`row-add-student-${topicName.replace(/\s+/g, "-")}`}
                        className="flex items-center gap-1.5 text-xs font-semibold text-indigo-700 px-2.5 py-1 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition"
                      >
                        <UserPlus className="w-3 h-3" />
                        <span className="hidden sm:inline">Add student</span>
                      </button>
                    </PopoverTrigger>
                    <StudentPicker
                      students={allStudents}
                      unlockedIds={studIds}
                      onUnlock={learnerIds => unlockForStudents(topicName, learnerIds)}
                    />
                  </Popover>
                </div>
              )}
            </div>

            {/* ── Expanded content ── */}
            {expanded && (
              <div className={`border-t px-4 pb-4 pt-3 ${dividerColor}`}>

                {/* Sub-topic list */}
                <div className="space-y-0.5 mb-4">
                  {(ch.sub_topics || []).map(st => {
                    const stCount = engagement.by_subtopic[st.sub_topic];
                    return (
                      <div
                        key={st.sub_topic}
                        className="flex items-center gap-2 text-sm text-slate-600 py-1.5 pl-6"
                      >
                        <span className="text-slate-300 select-none">—</span>
                        <span className="flex-1">{st.sub_topic}</span>
                        {stCount > 0 ? (
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  className="text-xs text-slate-600 font-medium tabular-nums cursor-default underline decoration-dotted decoration-slate-300"
                                  data-testid={`subtopic-engagement-count-${st.sub_topic.replace(/\s+/g, "-")}`}
                                >
                                  {stCount}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent
                                side="top"
                                className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 max-w-[220px]"
                                data-testid={`subtopic-engagement-tooltip-${st.sub_topic.replace(/\s+/g, "-")}`}
                              >
                                <div className="font-semibold mb-1 text-slate-300">Active in this sub-topic</div>
                                {(engagement.by_subtopic_students?.[st.sub_topic] || []).map((s) => (
                                  <div key={s.learner_id}>{s.name}</div>
                                ))}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <span className="text-xs text-slate-300 tabular-nums">–</span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* ── Actions section ── */}
                <div className={`pt-3 border-t ${dividerColor}`}>

                  {/* ─ State 2: Class unlocked → Re-lock button (+ optional confirm) ─ */}
                  {state === "class_unlocked" && (
                    <>
                      {relockConfirm === topicName ? (
                        /* Inline re-lock confirmation */
                        <div className="flex flex-wrap items-center gap-3 text-sm">
                          <span className="flex items-center gap-1.5 text-amber-700 text-xs">
                            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                            Re-locking removes all individual student unlocks too.
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setRelockConfirm(null)}
                              className="text-xs font-semibold text-slate-600 hover:text-slate-900 px-3 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-50 transition"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => execRelock(topicName)}
                              data-testid={`confirm-relock-${topicName.replace(/\s+/g, "-")}`}
                              disabled={saving}
                              className="text-xs font-semibold text-rose-700 hover:text-rose-900 px-3 py-1.5 border border-rose-300 rounded-lg hover:bg-rose-50 transition disabled:opacity-50"
                            >
                              Confirm re-lock
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleRelockClick(topicName)}
                          disabled={saving}
                          data-testid={`relock-${topicName.replace(/\s+/g, "-")}`}
                          className="flex items-center gap-1.5 text-xs font-semibold text-rose-700 hover:text-rose-900 px-3 py-1.5 border border-rose-300 rounded-lg hover:bg-rose-50 transition disabled:opacity-50"
                        >
                          <Lock className="w-3.5 h-3.5" />
                          Re-lock for class
                        </button>
                      )}
                    </>
                  )}

                  {/* ─ State 3: Students only → list + Unlock for class + Add student ─ */}
                  {state === "students_only" && (
                    <>
                      {/* Student unlock list */}
                      <div id={`student-unlocks-${topicName.replace(/\s+/g, "-")}`} className="mb-3">
                        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                          Student unlocks
                        </div>
                        <div className="space-y-1.5 pl-1">
                          {studIds.map(lid => {
                            const s = allStudents.find(x => x.learner_id === lid);
                            return (
                              <div key={lid} className="flex items-center gap-2">
                                <Avatar name={s?.name || lid} color="indigo" />
                                <span className="text-sm text-slate-800 flex-1">{s?.name || lid}</span>
                                <button
                                  onClick={() => removeStudentUnlock(topicName, lid)}
                                  data-testid={`remove-unlock-${lid}`}
                                  className="text-xs text-rose-500 hover:text-rose-700 hover:underline"
                                >
                                  remove
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => unlockForClass(topicName)}
                          disabled={saving}
                          data-testid={`unlock-class-${topicName.replace(/\s+/g, "-")}`}
                          className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 px-3 py-1.5 border border-slate-300 rounded-lg hover:bg-slate-50 transition disabled:opacity-50"
                        >
                          <Unlock className="w-3.5 h-3.5" />
                          Unlock for class
                        </button>
                        <Popover
                          open={pickerTopic === topicName}
                          onOpenChange={open => setPickerTopic(open ? topicName : null)}
                        >
                          <PopoverTrigger asChild>
                            <button
                              data-testid={`add-student-${topicName.replace(/\s+/g, "-")}`}
                              className="flex items-center gap-1.5 text-xs font-semibold text-indigo-700 hover:text-indigo-900 px-3 py-1.5 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition"
                            >
                              <UserPlus className="w-3.5 h-3.5" />
                              Add student
                            </button>
                          </PopoverTrigger>
                          <StudentPicker
                            students={allStudents}
                            unlockedIds={studIds}
                            onUnlock={learnerIds => unlockForStudents(topicName, learnerIds)}
                          />
                        </Popover>
                      </div>
                    </>
                  )}

                  {/* ─ State 1: Locked → Unlock for class + Unlock for a student ─ */}
                  {state === "locked" && (
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => unlockForClass(topicName)}
                        disabled={saving}
                        data-testid={`unlock-class-${topicName.replace(/\s+/g, "-")}`}
                        className="flex items-center gap-1.5 text-xs font-semibold text-slate-800 hover:text-slate-900 px-3 py-1.5 border border-slate-300 bg-white rounded-lg hover:bg-slate-50 transition disabled:opacity-50"
                      >
                        <Unlock className="w-3.5 h-3.5" />
                        Unlock for class
                      </button>
                      <Popover
                        open={pickerTopic === topicName}
                        onOpenChange={open => setPickerTopic(open ? topicName : null)}
                      >
                        <PopoverTrigger asChild>
                          <button
                            data-testid={`unlock-student-${topicName.replace(/\s+/g, "-")}`}
                            className="flex items-center gap-1.5 text-xs font-semibold text-indigo-700 hover:text-indigo-900 px-3 py-1.5 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition"
                          >
                            <UserPlus className="w-3.5 h-3.5" />
                            Unlock for a student
                          </button>
                        </PopoverTrigger>
                        <StudentPicker
                          students={allStudents}
                          onUnlock={learnerIds => unlockForStudents(topicName, learnerIds)}
                        />
                      </Popover>
                    </div>
                  )}

                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
